import React, { Fragment, useMemo, useState } from 'react';
import styled from 'styled-components';
import Table, { ORDER } from '../Table/Table';
import { TableBodyItem } from '../Table/Table.styles';
import { formatValueByType } from './chartUtils';

const DEFAULT_SORT_OPTIONS = {
  sortBy: 'totalEarnings',
  order: ORDER.DESC,
};

const TABLE_HEADER = [
  { label: 'Chatter', key: 'chatterName', sortable: true, fixedWidth: 18 },
  { label: 'Earnings', key: 'totalEarnings', sortable: true },
  { label: 'Avg Response', key: 'averageResponseTimeSeconds', sortable: true },
  { label: 'AI Usage', key: 'aiUsagePercentage', sortable: true },
  { label: 'Offers', key: 'offerCount', sortable: true },
  { label: 'Purchases', key: 'purchaseCount', sortable: true },
  { label: 'Conversion', key: 'ppvRate', sortable: true },
];

const OrganizationAnalyticsChatterTableItem = ({ item, chatterLabelMap }) => {
  const chatterName = chatterLabelMap[item.chatterId] || item.chatterId || '-';
  const totalAiMessages = Number(item.aiMessageCount || 0);
  const totalManualMessages = Number(item.manualMessageCount || 0);
  const totalMessages = totalAiMessages + totalManualMessages;
  const aiUsagePercentage = totalMessages ? (totalAiMessages / totalMessages) * 100 : 0;

  return (
    <Fragment>
      <TableBodyItem>
        <TableValue $isName>{chatterName}</TableValue>
      </TableBodyItem>
      <TableBodyItem>
        <TableValue>{formatValueByType(item.totalEarnings, 'currency')}</TableValue>
      </TableBodyItem>
      <TableBodyItem>
        <TableValue>{formatValueByType(item.averageResponseTimeSeconds, 'duration')}</TableValue>
      </TableBodyItem>
      <TableBodyItem>
        <TableValue>{formatValueByType(aiUsagePercentage, 'percent')}</TableValue>
      </TableBodyItem>
      <TableBodyItem>
        <TableValue>{formatValueByType(item.offerCount, 'number')}</TableValue>
      </TableBodyItem>
      <TableBodyItem>
        <TableValue>{formatValueByType(item.purchaseCount, 'number')}</TableValue>
      </TableBodyItem>
      <TableBodyItem>
        <TableValue>{formatValueByType(item.ppvRate, 'percent')}</TableValue>
      </TableBodyItem>
    </Fragment>
  );
};

const OrganizationAnalyticsChatterTable = ({ data = [], pending, chatterLabelMap = {} }) => {
  const [sortOptions, setSortOptions] = useState(DEFAULT_SORT_OPTIONS);

  const sortedData = useMemo(() => {
    const items = Array.isArray(data) ? [...data] : [];

    return items.sort((firstItem, secondItem) => {
      const firstChatterName = chatterLabelMap[firstItem.chatterId] || firstItem.chatterId || '';
      const secondChatterName = chatterLabelMap[secondItem.chatterId] || secondItem.chatterId || '';
      const firstAiMessages = Number(firstItem.aiMessageCount || 0);
      const secondAiMessages = Number(secondItem.aiMessageCount || 0);
      const firstManualMessages = Number(firstItem.manualMessageCount || 0);
      const secondManualMessages = Number(secondItem.manualMessageCount || 0);
      const firstAiUsagePercentage =
        firstAiMessages + firstManualMessages ? (firstAiMessages / (firstAiMessages + firstManualMessages)) * 100 : 0;
      const secondAiUsagePercentage =
        secondAiMessages + secondManualMessages
          ? (secondAiMessages / (secondAiMessages + secondManualMessages)) * 100
          : 0;

      const firstValue =
        sortOptions.sortBy === 'chatterName'
          ? firstChatterName
          : sortOptions.sortBy === 'aiUsagePercentage'
            ? firstAiUsagePercentage
            : firstItem[sortOptions.sortBy];

      const secondValue =
        sortOptions.sortBy === 'chatterName'
          ? secondChatterName
          : sortOptions.sortBy === 'aiUsagePercentage'
            ? secondAiUsagePercentage
            : secondItem[sortOptions.sortBy];

      const comparison =
        typeof firstValue === 'string' || typeof secondValue === 'string'
          ? String(firstValue || '').localeCompare(String(secondValue || ''))
          : Number(firstValue || 0) - Number(secondValue || 0);

      return sortOptions.order === ORDER.ASC ? comparison : comparison * -1;
    });
  }, [data, chatterLabelMap, sortOptions]);

  return (
    <Table
      headerInformation={TABLE_HEADER}
      dataLoading={pending}
      totalCount={sortedData.length}
      currentPage={1}
      perPage={Math.max(sortedData.length, 1)}
      setPerPage={() => {}}
      setPage={() => {}}
      sortOptions={sortOptions}
      setSortOptions={setSortOptions}
      emptyDataText="Select creators and chatters to load table data."
      hideFooter>
      {sortedData.map(item => (
        <OrganizationAnalyticsChatterTableItem key={item.chatterId} item={item} chatterLabelMap={chatterLabelMap} />
      ))}
    </Table>
  );
};

const TableValue = styled.p`
  font-weight: ${props => (props.$isName ? 500 : 300)};
`;

export default OrganizationAnalyticsChatterTable;
